import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Save,
  Send,
  History,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Input } from '../components/ui/Input.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { MediaPickerModal } from '../components/ui/MediaPickerModal.tsx';
import { RelationPickerModal } from '../components/ui/RelationPickerModal.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

type FieldType =
  | 'text'
  | 'rich_text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'image'
  | 'select'
  | 'color'
  | 'relation'
  | 'array';

interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: string[];
  itemType?: Exclude<FieldType, 'array'>;
  contentTypeId?: string;  // for type === 'relation'
  multiple?: boolean;      // for type === 'relation' — if true, value is UUID[]
}

interface Collection {
  id: string;
  name: string;
  slug: string;
  fieldsJson: FieldDefinition[];
}

interface Entry {
  id: string;
  collectionId: string;
  title: string;
  slug: string | null;
  status: 'draft' | 'published' | 'archived';
  dataJson: Record<string, unknown>;
  publishedAt: string | null;
  scheduledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface EntryRevision {
  id: string;
  title: string;
  createdBy: string | null;
  createdAt: string;
}

const STATUS_VARIANT = {
  draft: 'default',
  published: 'success',
  archived: 'warning',
} as const;

// ─── Shared input class ────────────────────────────────────────────────────────

const inputCls =
  'w-full border border-zinc-200 rounded-lg text-sm px-3 h-9 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white';
const labelCls = 'block text-sm font-medium text-zinc-700 mb-1';

// ─── Array repeater ──────────────────────────────────────────────────────────

function ArrayRepeaterInput({
  field,
  value,
  onChange,
}: {
  field: FieldDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const items = Array.isArray(value) ? (value as unknown[]) : [];
  const itemField: FieldDefinition = {
    key: field.key,
    label: field.label,
    type: field.itemType ?? 'text',
    options: field.options,
    required: false,
  };

  function updateItem(i: number, v: unknown) {
    const next = [...items];
    next[i] = v;
    onChange(next);
  }

  function removeItem(i: number) {
    onChange(items.filter((_, idx) => idx !== i));
  }

  function addItem() {
    const defaultVal = itemField.type === 'boolean' ? false : itemField.type === 'number' ? 0 : '';
    onChange([...items, defaultVal]);
  }

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex-1">
            <FieldInput field={itemField} value={item} onChange={(v) => updateItem(i, v)} />
          </div>
          <button
            type="button"
            onClick={() => removeItem(i)}
            className="text-xs text-zinc-400 hover:text-red-500 px-1"
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="text-xs text-zinc-500 hover:text-zinc-800 transition-colors"
      >
        + Add item
      </button>
    </div>
  );
}

// ─── Relation field input ─────────────────────────────────────────────────────

function RelationFieldInput({
  field,
  value,
  onChange,
  spaceId,
}: {
  field: FieldDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
  spaceId?: string;
  collectionId?: string;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const isMultiple = field.multiple ?? false;

  const selectedIds: string[] = isMultiple
    ? Array.isArray(value) ? (value as string[]) : []
    : typeof value === 'string' && value ? [value] : [];

  function handleConfirm(ids: string[]) {
    onChange(isMultiple ? ids : (ids[0] ?? null));
    setPickerOpen(false);
  }

  function removeId(id: string) {
    if (isMultiple) {
      onChange((value as string[]).filter((x) => x !== id));
    } else {
      onChange(null);
    }
  }

  return (
    <div className="space-y-2">
      {field.contentTypeId && spaceId && (
        <RelationPickerModal
          open={pickerOpen}
          spaceId={spaceId}
          targetCollectionId={field.contentTypeId}
          multiple={isMultiple}
          selectedIds={selectedIds}
          onConfirm={handleConfirm}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-xs text-zinc-700 font-mono"
            >
              {id.slice(0, 8)}…
              <button
                type="button"
                onClick={() => removeId(id)}
                className="text-zinc-400 hover:text-red-500"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        disabled={!field.contentTypeId || !spaceId}
        className="px-3 h-8 text-xs font-medium border border-zinc-200 rounded-lg hover:bg-zinc-50 text-zinc-700 transition-colors disabled:opacity-40"
      >
        {selectedIds.length === 0
          ? 'Select →'
          : isMultiple
          ? 'Add more →'
          : 'Change →'}
      </button>

      {!field.contentTypeId && (
        <p className="text-xs text-zinc-400">No target collection configured for this field.</p>
      )}
    </div>
  );
}

// ─── Field input ───────────────────────────────────────────────────────────────

function FieldInput({
  field,
  value,
  onChange,
  spaceId,
  collectionId,
}: {
  field: FieldDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
  spaceId?: string;
  collectionId?: string;
}) {
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  switch (field.type) {
    case 'text':
      return (
        <input
          className={inputCls}
          type="text"
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'image':
      return (
        <div className="space-y-2">
          <MediaPickerModal
            open={mediaPickerOpen}
            onClose={() => setMediaPickerOpen(false)}
            onSelect={(url) => {
              onChange(url);
              setMediaPickerOpen(false);
            }}
          />
          {!!value && (
            <div className="rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50 h-24 flex items-center justify-center">
              <img src={String(value)} alt="" className="max-h-full max-w-full object-contain" />
            </div>
          )}
          <div className="flex gap-2">
            <input
              className={inputCls}
              type="text"
              value={String(value ?? '')}
              placeholder="https://… or choose from library"
              onChange={(e) => onChange(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setMediaPickerOpen(true)}
              className="shrink-0 px-3 h-9 text-xs font-medium border border-zinc-200 rounded-lg hover:bg-zinc-50 text-zinc-700 whitespace-nowrap transition-colors"
            >
              Choose
            </button>
          </div>
        </div>
      );

    case 'rich_text':
      return (
        <textarea
          className="w-full border border-zinc-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white min-h-[120px] resize-y"
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder="HTML content…"
        />
      );

    case 'number':
      return (
        <input
          className={inputCls}
          type="number"
          value={value !== undefined && value !== null ? String(value) : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );

    case 'boolean':
      return (
        <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer mt-1">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded border-zinc-300 w-4 h-4"
          />
          {field.label}
        </label>
      );

    case 'date':
      return (
        <input
          className={inputCls}
          type="date"
          value={value ? String(value).slice(0, 10) : ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );

    case 'datetime':
      return (
        <input
          className={inputCls}
          type="datetime-local"
          value={value ? String(value).slice(0, 16) : ''}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
        />
      );

    case 'select':
      return (
        <select
          className={inputCls}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— Select —</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );

    case 'color':
      return (
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={String(value ?? '#000000')}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-12 rounded border border-zinc-200 cursor-pointer p-0.5"
          />
          <input
            className={`${inputCls} flex-1 font-mono`}
            type="text"
            value={String(value ?? '')}
            placeholder="#000000"
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );

    case 'array':
      return <ArrayRepeaterInput field={field} value={value} onChange={onChange} />;

    case 'relation':
      return (
        <RelationFieldInput
          field={field}
          value={value}
          onChange={onChange}
          spaceId={spaceId}
          collectionId={collectionId}
        />
      );

    default:
      return null;
  }
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsEntryEditorPage() {
  const { spaceId, colId, entryId } = useParams<{
    spaceId: string;
    colId: string;
    entryId: string;
  }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const BASE = `/api/v1/cms/spaces/${spaceId}/collections/${colId}`;
  const ENTRY_BASE = `${BASE}/entries/${entryId}`;

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [dirty, setDirty] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<EntryRevision | null>(null);

  const { data: collection } = useQuery<Collection>({
    queryKey: ['cms-collection', spaceId, colId],
    queryFn: () => api.get(BASE).then((r) => r.data),
    enabled: !!spaceId && !!colId,
  });

  const { data: entry, isLoading } = useQuery<Entry>({
    queryKey: ['cms-entry', spaceId, colId, entryId],
    queryFn: () => api.get(ENTRY_BASE).then((r) => r.data),
    enabled: !!spaceId && !!colId && !!entryId,
  });

  const { data: revisions = [] } = useQuery<EntryRevision[]>({
    queryKey: ['cms-entry-revisions', spaceId, colId, entryId],
    queryFn: () => api.get(`${ENTRY_BASE}/revisions`).then((r) => r.data),
    enabled: !!spaceId && !!colId && !!entryId && showRevisions,
  });

  // Sync form state when entry loads
  useEffect(() => {
    if (entry) {
      setTitle(entry.title);
      setSlug(entry.slug ?? '');
      setFormData(entry.dataJson ?? {});
      setDirty(false);
    }
  }, [entry]);

  function setField(key: string, value: unknown) {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      api
        .patch(ENTRY_BASE, {
          title,
          slug: slug.trim() || undefined,
          dataJson: formData,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-entry', spaceId, colId, entryId] });
      qc.invalidateQueries({ queryKey: ['cms-entries', spaceId, colId] });
      qc.invalidateQueries({ queryKey: ['cms-entry-revisions', spaceId, colId, entryId] });
      toast.success('Saved');
      setDirty(false);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      await api.patch(ENTRY_BASE, {
        title,
        slug: slug.trim() || undefined,
        dataJson: formData,
      });
      return api.post(`${ENTRY_BASE}/publish`).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-entry', spaceId, colId, entryId] });
      qc.invalidateQueries({ queryKey: ['cms-entries', spaceId, colId] });
      qc.invalidateQueries({ queryKey: ['cms-entry-revisions', spaceId, colId, entryId] });
      toast.success('Published');
      setDirty(false);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const restoreMutation = useMutation({
    mutationFn: (revId: string) =>
      api.post(`${ENTRY_BASE}/revisions/${revId}/restore`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-entry', spaceId, colId, entryId] });
      qc.invalidateQueries({ queryKey: ['cms-entry-revisions', spaceId, colId, entryId] });
      setRestoreTarget(null);
      setShowRevisions(false);
      toast.success('Entry restored to selected revision');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  if (isLoading || !entry || !collection) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-48 bg-zinc-100 rounded animate-pulse" />
        <div className="h-40 bg-zinc-50 rounded-xl animate-pulse" />
      </div>
    );
  }

  const fields = collection.fieldsJson ?? [];

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <Link
          to={`/spaces/${spaceId}/collections/${colId}/entries`}
          className="hover:text-zinc-600 flex items-center gap-1"
        >
          <ChevronLeft size={13} /> {collection.name}
        </Link>
        <span>/</span>
        <span className="text-zinc-700 font-medium truncate">{title || 'Untitled'}</span>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/spaces/${spaceId}/collections/${colId}/entries`)}
          className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-zinc-900 truncate">{title || 'Untitled'}</h1>
            <Badge variant={STATUS_VARIANT[entry.status] ?? 'default'}>{entry.status}</Badge>
            {dirty && <span className="text-xs text-amber-500 font-medium">Unsaved changes</span>}
          </div>
          <p className="text-xs text-zinc-400 mt-0.5">
            {collection.name}
            {entry.slug && (
              <span className="ml-2 font-mono text-zinc-500">
                /c/{collection.slug}/{entry.slug}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowRevisions(true)}
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800 px-2 py-1.5 rounded-md hover:bg-zinc-100 transition-colors"
          >
            <History size={13} /> Revisions
          </button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || publishMutation.isPending}
          >
            <Save size={14} className="mr-1" />
            Save
          </Button>
          {entry.status !== 'published' && (
            <Button
              size="sm"
              onClick={() => publishMutation.mutate()}
              disabled={saveMutation.isPending || publishMutation.isPending}
            >
              <Send size={14} className="mr-1" />
              Publish
            </Button>
          )}
        </div>
      </div>

      {/* Title + Slug */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
        <Input
          label="Title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setDirty(true);
          }}
        />
        <div>
          <Input
            label="Slug"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setDirty(true);
            }}
            placeholder="auto-generated from title"
            className="font-mono text-xs"
          />
          <p className="text-xs text-zinc-400 mt-1">
            Used in the portal URL: /c/{collection.slug}/
            <span className="font-mono">{slug || '…'}</span>
          </p>
        </div>
      </div>

      {/* Dynamic fields */}
      {fields.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-zinc-700">Content</h2>
          {fields.map((field) => (
            <div key={field.key}>
              {field.type !== 'boolean' && (
                <label className={labelCls}>
                  {field.label}
                  {field.required && <span className="text-red-400 ml-0.5">*</span>}
                  {field.type === 'array' && field.itemType && (
                    <span className="ml-1.5 text-zinc-400 font-normal text-xs">
                      (list of {field.itemType})
                    </span>
                  )}
                </label>
              )}
              <FieldInput
                field={field}
                value={formData[field.key]}
                onChange={(v) => setField(field.key, v)}
                spaceId={spaceId}
                collectionId={colId}
              />
            </div>
          ))}
        </div>
      )}

      {/* Meta */}
      <div className="text-xs text-zinc-400 space-y-0.5">
        <p>Created: {new Date(entry.createdAt).toLocaleString('en-GB')}</p>
        <p>Updated: {new Date(entry.updatedAt).toLocaleString('en-GB')}</p>
        {entry.publishedAt && (
          <p>Published: {new Date(entry.publishedAt).toLocaleString('en-GB')}</p>
        )}
        {entry.scheduledAt && (
          <p>Scheduled: {new Date(entry.scheduledAt).toLocaleString('en-GB')}</p>
        )}
      </div>

      {/* Revisions Modal */}
      <Modal open={showRevisions} onClose={() => setShowRevisions(false)} title="Revisions">
        {revisions.length === 0 ? (
          <p className="text-sm text-zinc-500">No revisions yet.</p>
        ) : (
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {revisions.map((rev, idx) => (
              <div
                key={rev.id}
                className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-zinc-50"
              >
                <div>
                  <p className="text-sm text-zinc-700">{rev.title || 'Untitled'}</p>
                  <p className="text-xs text-zinc-400">
                    {new Date(rev.createdAt).toLocaleString('en-GB')}
                    {idx === 0 && <span className="ml-2 text-emerald-600 font-medium">Latest</span>}
                    {rev.createdBy && (
                      <span className="ml-2 text-zinc-400">by {rev.createdBy}</span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => setRestoreTarget(rev)}
                  className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 px-2 py-1 rounded hover:bg-zinc-100 transition-colors"
                >
                  <RotateCcw size={12} /> Restore
                </button>
              </div>
            ))}
          </div>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowRevisions(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Restore confirm */}
      <ConfirmDialog
        open={!!restoreTarget}
        title="Restore revision?"
        message={`This will replace the current entry with the version from ${
          restoreTarget ? new Date(restoreTarget.createdAt).toLocaleString('en-GB') : ''
        }. The entry will be set back to draft.`}
        confirmLabel="Restore"
        variant="danger"
        loading={restoreMutation.isPending}
        onConfirm={() => restoreTarget && restoreMutation.mutate(restoreTarget.id)}
        onCancel={() => setRestoreTarget(null)}
      />
    </div>
  );
}
